import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';
import { VocabularyPicker, type PickerOption } from '@shared/ui/picker';

import { flattenTree } from '@features/taxonomy/_shared/tree';

import { copy } from './copy';
import { errors } from './errors';
import { shelfFormSchema, type ShelfFormValues } from './forms.schema';
import { useCategories, useContentConfigurations, useTags, useTitleOptions } from './queries';
import { lookup, OFF_HOME_SOURCE, paramKindFor, readCsvSetting, sourceLabel } from './Shelves.vocabulary';
import type { CategoryNode, Shelf, ShelfDraft, ShelfSurface } from './types';

/**
 * One dialog, two modes — edit and create.
 *
 * **Editing happens here because the sitemap carries one shelf route.** There
 * is no `/shelves/:id`, so the choice was an in-place dialog or no edit surface
 * at all, and «management» is not satisfied by a list an operator can only
 * delete from.
 *
 * **There is no layout field.** The catalog places a shelf's shape in its
 * declared identity in configuration rather than in a runtime choice: «Home
 * shelves — public» names `hero slider` as a *shelf*, not as a setting on one.
 * The four layouts still ship — they are how the home page renders — they are
 * simply not this surface's to set.
 *
 * **The source list is the configured value, read at render.** A ninth source
 * registered in the runtime configuration surface appears here with no release;
 * one this console has no wording for renders as its own key rather than being
 * dropped.
 *
 * **The parameter panel swaps with the source and only one is ever mounted.**
 * Three of the configured sources resolve against something and the rest
 * resolve on their own — so the dialog shows the matching picker, or a plain
 * sentence where nothing is to be written. The inapplicable panels are not
 * rendered at all, which is the strongest form of the `hidden` rule: a field
 * nobody can see is also a field `Tab` cannot reach.
 */

const EMPTY: readonly PickerOption[] = [];

/** The Arabic wording for a state that is not `PUBLISHED`; an unknown state renders as its own key. */
function publishStateLabel(state: string): string {
  return lookup(copy.dialog.publishState, state) ?? state;
}

/**
 * The genre tree is any depth; the picker's vocabulary is its flattening.
 *
 * **The label is the full ancestor path, not the node's own name.** Two nodes
 * called «دراما» under two parents are one word twice in this list, and an
 * operator cannot tell which branch a shelf is about to be bound to. The edit
 * dialog on `/taxonomy/categories` already shows the path for exactly this
 * reason, and `flattenTree` is the function that composes it — so this picker
 * reads from it rather than keeping a second, shorter walk of the same tree.
 */
function flattenCategories(nodes: readonly CategoryNode[] | undefined): PickerOption[] {
  if (!nodes) return [];
  return flattenTree(nodes).map((node) => ({ id: node.id, label: node.path }));
}

export function ShelfDialog({
  open,
  surface,
  shelf,
  busy,
  error,
  onSubmit,
  onClose,
}: {
  open: boolean;
  surface: ShelfSurface;
  /** `null` is create — the same dialog with an empty form and a different heading. */
  shelf: Shelf | null;
  busy: boolean;
  error: unknown;
  onSubmit: (draft: ShelfDraft) => void;
  onClose: () => void;
}) {
  const headingId = useId();
  const titleId = useId();
  const titleHintId = useId();
  const sourceId = useId();

  const configurations = useContentConfigurations();
  const categories = useCategories();
  const tags = useTags();

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors: fieldErrors },
  } = useForm<ShelfFormValues>({
    resolver: zodResolver(shelfFormSchema),
    defaultValues: { title: '', source: '', paramValues: [] },
  });

  const source = watch('source');
  const paramKind = paramKindFor(source);

  // The catalogue is the console's largest list and only one panel needs it.
  const titles = useTitleOptions(open && paramKind === 'CONTENT_ITEM_SET');

  // Memoised because the seeding effect below depends on it. `readCsvSetting`
  // splits a string, so it hands back a **new array on every render** — as a
  // raw dependency it would re-run the effect, whose `reset` renders again, and
  // the dialog would never settle.
  const sources = useMemo(() => readCsvSetting(configurations.data, 'SHELF_SOURCES'), [configurations.data]);

  const options: readonly PickerOption[] = useMemo(() => {
    if (paramKind === 'CATEGORY') return flattenCategories(categories.data?.items);
    if (paramKind === 'TAG') return (tags.data?.items ?? []).map((tag) => ({ id: tag.id, label: tag.name }));
    if (paramKind === 'CONTENT_ITEM_SET') {
      // The publish state travels with the option. A public shelf may point at
      // a draft — the catalog's own answer is that no source filters on publish
      // state and the hand-picked row is deliberately manual — but the landing
      // page drops the card, so the shelf silently shrinks at render. Naming the
      // state here is what turns that into a choice the operator made.
      return (titles.data?.items ?? []).map((item) => ({
        id: item.id,
        label: item.title,
        note: item.publish_state === 'PUBLISHED' ? undefined : publishStateLabel(item.publish_state),
      }));
    }
    return EMPTY;
  }, [paramKind, categories.data, tags.data, titles.data]);

  // The form is seeded from the row on every open rather than once on mount, so
  // the dialog always opens on what the table actually shows — and a search
  // term left over from another shelf never survives into this one.
  useEffect(() => {
    if (!open) return;

    reset({
      title: shelf?.title ?? '',
      source: shelf?.source ?? sources[0] ?? '',
      paramValues: shelf?.sourceParam?.values ?? [],
    });
  }, [open, shelf, reset, sources]);

  const formError = error instanceof ApiError ? resolveErrorCopy(error.code, error.status, errors) : null;

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      className="max-w-[28rem]"
      onClose={(returnValue) => {
        if (returnValue !== 'save') onClose();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element instead of creating the shelf the
          // operator has just decided not to create.
          if (isCancelledSubmit(event)) return;
          void handleSubmit((values) => {
            onSubmit({ title: values.title, source: values.source, paramValues: values.paramValues });
          })(event);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {shelf ? copy.dialog.headingEdit : copy.dialog.headingCreate}
          </h2>
          <p className="mb-0 text-xs text-ink-muted">{copy.surfaces[surface].heading}</p>
        </div>

        {formError ? (
          <p role="alert" className="mb-0 rounded-sm border-s-4 border-warning bg-warning-soft p-3 text-sm">
            {formError.message}
          </p>
        ) : null}

        <div className="grid gap-2">
          <label htmlFor={titleId} className="text-sm font-medium">
            {copy.dialog.title}
          </label>
          <input
            id={titleId}
            required
            aria-describedby={titleHintId}
            aria-invalid={fieldErrors.title ? true : undefined}
            className="hit-target rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink"
            {...register('title')}
          />
          <p id={titleHintId} className="mb-0 text-xs text-ink-muted">
            {copy.dialog.titleHint}
          </p>
          {fieldErrors.title ? (
            <p className="mb-0 text-xs text-danger">
              {lookup(copy.validation, fieldErrors.title.message ?? '') ?? ''}
            </p>
          ) : null}
        </div>

        {/*
         * THE PARAMETER BELONGS TO THE SOURCE, SO IT DOES NOT SURVIVE A CHANGE OF ONE.
         *
         * Without this the previous source's value stayed in the form after the picker
         * that produced it was gone, and the shelf could not be re-sourced at all:
         *
         *   • **to a parameterless source** the schema refused `param.forbidden` — and
         *     that message renders inside the parameter panel, which a parameterless
         *     source does not draw. So «احفظ» did nothing, said nothing, and went on
         *     doing nothing however many times it was pressed;
         *   • **to a different parameterised kind** the stale ids were sent under the
         *     new kind. Switching a hand-picked shelf to `tag` sent two ContentItem
         *     ULIDs as `sourceParamKind: TAG`, and the service answered
         *     `404 CONTENT_TAG_NOT_FOUND` — correctly, because a tag with a title's id
         *     is exactly what the vocabulary check exists to refuse. The picker even
         *     drew the raw ULIDs as chips, which is what the operator saw.
         *
         * Clearing is the honest reading of the change: a parameter authored against
         * one vocabulary names nothing in another.
         */}
        <div className="grid gap-2">
          <label htmlFor={sourceId} className="text-sm font-medium">
            {copy.dialog.source}
          </label>
          <select
            id={sourceId}
            required
            className="hit-target rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink"
            {...register('source', {
              // Clearing on the CONTROL, not in an effect on the watched value:
              // an effect runs in the same commit as the seeding `reset` above
              // and reads the source from the render before it, so it would
              // wipe the parameter the dialog had just opened on. `onChange`
              // here fires only when a person changes the select.
              onChange: () => setValue('paramValues', [], { shouldValidate: false }),
            })}
          >
            {sources.map((member) => (
              <option key={member} value={member}>
                {sourceLabel(member)}
              </option>
            ))}
          </select>
          {source === OFF_HOME_SOURCE ? (
            <p className="mb-0 text-xs text-ink-muted">{copy.dialog.relatedHint}</p>
          ) : null}
        </div>

        {/*
          One panel, chosen by the source. The three scoped kinds share one
          shape — a picker with a search — because the parameter is a choice
          from a set that already exists, never a typed string: a free-text
          field would let an operator author a shelf pointing at a category the
          platform does not hold, and the shelf would render empty with nothing
          on screen explaining why.
        */}
        {paramKind ? (
          <div className="grid gap-2">
            <span className="text-sm font-medium">{copy.dialog.params[paramKind].label}</span>

            <Controller
              control={control}
              name="paramValues"
              render={({ field }) => (
                <VocabularyPicker
                  // `single` replaces — a shelf scoped to two categories is a
                  // shelf with two sources. `multi` appends, because the
                  // hand-picked set is ordered and a new title joins its end.
                  mode={paramKind === 'CONTENT_ITEM_SET' ? 'multi' : 'single'}
                  options={options}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={busy}
                  copy={{
                    searchLabel: copy.dialog.params[paramKind].searchLabel,
                    searchPlaceholder: copy.dialog.params[paramKind].searchPlaceholder,
                    resultCount: copy.dialog.picker.resultCount,
                    noResults: copy.dialog.picker.noResults,
                    remove: copy.dialog.picker.remove,
                  }}
                />
              )}
            />

            <p className="mb-0 text-xs text-ink-muted">{copy.dialog.params[paramKind].hint}</p>
            {/*
              The catalogue does not fit in one page and this picker searches
              only what it loaded, so the reach of the search is stated rather
              than left to be discovered. Without it «لا نتيجة تطابق البحث» is a
              silent wrong answer: the title exists, it is simply past the page.
            */}
            {paramKind === 'CONTENT_ITEM_SET' && titles.data?.has_next ? (
              <p className="mb-0 text-xs text-ink-muted">
                {copy.dialog.params.CONTENT_ITEM_SET.searchScope(
                  formatNumber(titles.data.items.length),
                  formatNumber(titles.data.total),
                )}
              </p>
            ) : null}
            {fieldErrors.paramValues ? (
              <p className="mb-0 text-xs text-danger">
                {lookup(copy.validation, fieldErrors.paramValues.message ?? '') ?? ''}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="mb-0 text-xs text-ink-muted">{copy.dialog.noParam}</p>
        )}

        {/*
          Design-system §9.2: the committing action first from the reading-start
          edge, then «ألغِ» as a ghost **button** — it performs an action rather
          than navigating.

          `formNoValidate` on cancel is not decoration. Both buttons in a
          `method="dialog"` form are submits, and a submit runs constraint
          validation — so without it the browser blocks the *exit* and demands a
          value from an operator who has just decided not to supply one, turning
          this dialog, which holds two `required` controls, into a trap whose
          only signposted exits are `Esc` and the backdrop.
        */}
        <div className="mt-1 flex flex-wrap gap-3">
          <button
            type="submit"
            value="save"
            disabled={busy}
            className="hit-target w-full rounded-sm bg-accent px-5 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto"
          >
            {copy.dialog.save}
          </button>
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onClose}
            className="hit-target w-full rounded-sm px-5 text-sm text-ink hover:bg-hover-bg sm:w-auto"
          >
            {copy.dialog.cancel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
